import { PiggyBankCircleIcon } from "../../../components/SvgIcons/PiggyBankCircleIcon";
import { AlertMessageLink } from "../../../components/AlertMessage/AlertMessageLink";
import {
  AlertMessageCondition,
  AlertMessageHierarchyKeys,
  type AlertMessageType,
} from "./types";
import { routes } from "../../routes";
import { DefaultNoSavedDrugsLink } from "./components/noSavedDrugsLinks";
import { DefaultNoSavedPharmaciesLink } from "./components/noSavedPharmaciesLinks";
import { DefaultOneSavedPharmacyLink } from "./components/oneSavedPharmacyLinks";
import { DefaultOneSavedPharmacyStaleAccountLink } from "./components/oneSavedPharmacyStaleAccountLinks";

/**
 * @constant
 * Object map with the intended location as a key and a ranked list of alert message types ordered from most import to least important.
 * Only the lowest index, active === true message will be displayed for the given key/location.
 */
export const alertMessageHierarchy: Record<
  AlertMessageHierarchyKeys,
  AlertMessageCondition[]
> = {
  [AlertMessageHierarchyKeys.SUMMARY_PAGE__PHARMACY_SECTION]: [
    AlertMessageCondition.NoSavedPharmacies,
    AlertMessageCondition.OneSavedPharmacyStaleAccount,
    AlertMessageCondition.OneSavedPharmacy,
    AlertMessageCondition.StaleAccountPharmacies,
    AlertMessageCondition.NoInNetworkSavedPharmacies,
    AlertMessageCondition.NoPreferredSavedPharmacies,
  ],
  [AlertMessageHierarchyKeys.PLAN_DETAILS__PHARMACY_SECTION]: [
    AlertMessageCondition.NoSavedPharmacies,
    AlertMessageCondition.NoSavedDrugs,
    AlertMessageCondition.OneSavedPharmacy,
    AlertMessageCondition.NoInNetworkSavedPharmacies,
    AlertMessageCondition.NoPreferredSavedPharmacies,
  ],
  [AlertMessageHierarchyKeys.PLAN_COMPARISON_NETWORK_STATUS]: [
    AlertMessageCondition.NoInNetworkSavedPharmacies,
    AlertMessageCondition.NoPreferredSavedPharmacies,
  ],
};

export const defaultAlertMessageState: Record<
  AlertMessageCondition,
  AlertMessageType
> = {
  [AlertMessageCondition.NoSavedPharmacies]: {
    key: AlertMessageCondition.NoSavedPharmacies,
    active: false,
    title: t => t("lcp.messages.add_pharmacies.headline"),
    body: t => t("lcp.messages.add_pharmacies.description"),
    action: (t, alertMessageContext) => (
      <DefaultNoSavedPharmaciesLink
        alertMessageContext={alertMessageContext}
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        treatAsButton
      >
        {t("lcp.messages.add_pharmacies.button")}
      </DefaultNoSavedPharmaciesLink>
    ),
  },
  [AlertMessageCondition.NoSavedDrugs]: {
    key: AlertMessageCondition.NoSavedDrugs,
    active: false,
    title: t => t("lcp.messages.add_drugs.headline"),
    body: t => t("lcp.messages.add_drugs.description"),
    action: (t, alertMessageContext) => (
      <DefaultNoSavedDrugsLink
        alertMessageContext={alertMessageContext}
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        treatAsButton
      >
        {t("lcp.messages.add_drugs.button")}
      </DefaultNoSavedDrugsLink>
    ),
  },
  [AlertMessageCondition.OneSavedPharmacyStaleAccount]: {
    active: false,
    key: AlertMessageCondition.OneSavedPharmacyStaleAccount,
    title: t => t("lcp.messages.add_more_pharmacies_stale.headline"),
    body: t => t("lcp.messages.add_more_pharmacies_stale.description"),
    action: (t, alertMessageContext) => (
      <DefaultOneSavedPharmacyStaleAccountLink
        alertMessageContext={alertMessageContext}
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        treatAsButton
      >
        {t("lcp.messages.add_more_pharmacies_stale.button")}
      </DefaultOneSavedPharmacyStaleAccountLink>
    ),
  },
  [AlertMessageCondition.OneSavedPharmacy]: {
    active: false,
    key: AlertMessageCondition.OneSavedPharmacy,
    title: t => t("lcp.messages.add_more_pharmacies_standard.headline"),
    body: t => t("lcp.messages.add_more_pharmacies_standard.description"),
    action: (t, alertMessageContext) => (
      <DefaultOneSavedPharmacyLink
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        alertMessageContext={alertMessageContext}
        treatAsButton
      >
        {t("lcp.messages.add_more_pharmacies_standard.button")}
      </DefaultOneSavedPharmacyLink>
    ),
  },
  [AlertMessageCondition.StaleAccountPharmacies]: {
    action: undefined,
    active: false,
    body: t => t("summary_page.pharmacies.alert.no_pharmacies"),
    icon: <PiggyBankCircleIcon />,

    key: AlertMessageCondition.StaleAccountPharmacies,
    title: undefined,
  },
  [AlertMessageCondition.StaleAccountDrugs]: {
    action: undefined,
    active: false,
    body: t => t("summary_page.drugs.alert.no_drugs"),
    icon: <PiggyBankCircleIcon />,
    key: AlertMessageCondition.StaleAccountDrugs,
    title: undefined,
  },
  [AlertMessageCondition.NoInNetworkSavedPharmacies]: {
    key: AlertMessageCondition.NoInNetworkSavedPharmacies,
    active: false,
    title: t => t("lcp.messages.in_network_pharmacies.headline"),
    body: t => t("lcp.messages.in_network_pharmacies.description"),
    action: (t, alertMessageContext) => (
      <AlertMessageLink
        to={{
          pathname: routes.inNetworkPharmacy.replace(
            ":id",
            alertMessageContext?.planId || ""
          ),
          state: alertMessageContext?.routerState,
          search: alertMessageContext?.search,
        }}
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        condition={AlertMessageCondition.NoInNetworkSavedPharmacies}
        treatAsButton
      >
        {t("lcp.messages.in_network_pharmacies.button")}
      </AlertMessageLink>
    ),
  },
  [AlertMessageCondition.NoPreferredSavedPharmacies]: {
    active: false,
    key: AlertMessageCondition.NoPreferredSavedPharmacies,
    title: t => t("lcp.messages.preferred_pharmacies.headline"),
    body: t => t("lcp.messages.preferred_pharmacies.description"),
    action: (t, alertMessageContext) => (
      <AlertMessageLink
        to={{
          pathname: routes.inNetworkPharmacy.replace(
            ":id",
            alertMessageContext?.planId || ""
          ),
          state: alertMessageContext?.routerState,
          search: alertMessageContext?.search,
        }}
        className="ds-c-button ds-c-button--solid ds-c-button--alternate"
        condition={AlertMessageCondition.NoPreferredSavedPharmacies}
        treatAsButton
      >
        {t("lcp.messages.preferred_pharmacies.button")}
      </AlertMessageLink>
    ),
  },
};
